import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import type { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, type SupportedLanguage } from "@pimo/pimo-l10n";
import { useId, useState } from "react";

import {
  type AdminClientCreatedRange,
  isAdminClientCreatedRangeActive,
  validateAdminClientCreatedRange,
} from "../helpers/admin-client-created-range";
import {
  type AdminOverviewFilters,
  type AdminOverviewHealth,
  type AdminOverviewPlan,
  type AdminOverviewStatus,
  createDefaultAdminOverviewFilters,
} from "../helpers/filter-admin-overview";

export interface AdminOverviewFiltersProps {
  filters: AdminOverviewFilters;
  language: SupportedLanguage;
  totalClients: number;
  matchedClients: number;
}

export type AdminOverviewFiltersEvent = "filters-changed";

const PLANS: readonly AdminOverviewPlan[] = [
  "basic",
  "advanced",
  "professional",
  "no-plan",
  "unknown-plan",
];
const STATUSES: readonly AdminOverviewStatus[] = ["trial", "active", "expired"];
const HEALTH: readonly AdminOverviewHealth[] = [
  "reachable",
  "unreachable",
  "unchecked",
];

/**
 * Standalone controlled toolbar. Applied criteria belong to the parent;
 * only expansion and date drafts are local. No fetching or result filtering.
 */
export const AdminOverviewFiltersToolbar: PimoReactComponent<
  AdminOverviewFiltersProps,
  AdminOverviewFiltersEvent,
  AdminOverviewFilters
> = ({ filters, fireEvent, language, totalClients, matchedClients }) => {
  const theme = useTheme();
  const id = useId();
  const [expanded, setExpanded] = useState(false);
  const [dates, setDates] = useState({
    applied: { ...filters.createdRange },
    draft: { ...filters.createdRange },
    badFrom: false,
    badThrough: false,
  });

  // Compare bounds, not object identity: unrelated controlled updates must not
  // discard invalid drafts. An actual external range change replaces them.
  if (
    dates.applied.from !== filters.createdRange.from ||
    dates.applied.through !== filters.createdRange.through
  ) {
    setDates({
      applied: { ...filters.createdRange },
      draft: { ...filters.createdRange },
      badFrom: false,
      badThrough: false,
    });
  }

  const t = (key: string, params?: Record<string, string | number>) =>
    getLocalizedMessage(language, `adminPanel.filters.${key}`, params);
  const dateError = dates.badFrom
    ? "invalid-from"
    : dates.badThrough
      ? "invalid-through"
      : validateAdminClientCreatedRange(dates.draft);
  const activeCount = [
    !!filters.query.trim(),
    filters.plans.length > 0,
    filters.statuses.length > 0,
    filters.health.length > 0,
    filters.appPresence !== "all",
    isAdminClientCreatedRangeActive(filters.createdRange),
  ].filter(Boolean).length;
  const hasDraft =
    !!dateError ||
    dates.draft.from !== filters.createdRange.from ||
    dates.draft.through !== filters.createdRange.through;
  const conflict =
    filters.appPresence === "without-apps" &&
    (filters.statuses.length > 0 || filters.health.length > 0);

  const change = (patch: Partial<AdminOverviewFilters>) =>
    fireEvent?.("filters-changed", { ...filters, ...patch });

  function editDate(
    bound: keyof AdminClientCreatedRange,
    value: string,
    badInput: boolean
  ) {
    const draft = { ...dates.draft, [bound]: value };
    const badFrom = bound === "from" ? badInput : dates.badFrom;
    const badThrough = bound === "through" ? badInput : dates.badThrough;
    setDates({ ...dates, draft, badFrom, badThrough });
    if (!badFrom && !badThrough && !validateAdminClientCreatedRange(draft)) {
      change({ createdRange: draft });
    }
  }

  function multiSelect<T extends string>(
    key: "plans" | "statuses" | "health",
    options: readonly T[],
    value: readonly T[]
  ) {
    const labelId = `${id}-${key}-label`;
    return (
      <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
        <InputLabel id={labelId}>{t(key)}</InputLabel>
        <Select
          id={`${id}-${key}`}
          labelId={labelId}
          label={t(key)}
          multiple
          value={[...value]}
          onChange={(event) => {
            const selected = event.target.value;
            change({
              [key]:
                typeof selected === "string" ? selected.split(",") : selected,
            });
          }}
          renderValue={(selected) =>
            selected.map((option) => t(`options.${option}`)).join(", ")
          }
          sx={{
            "& .MuiSelect-select": {
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            },
          }}
          MenuProps={{ PaperProps: { sx: { maxWidth: "calc(100vw - 32px)" } } }}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option} sx={{ whiteSpace: "normal" }}>
              <Checkbox
                checked={value.includes(option)}
                tabIndex={-1}
                disableRipple
              />
              <ListItemText primary={t(`options.${option}`)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <Paper
      component="section"
      aria-label={t("title")}
      variant="outlined"
      sx={{
        minWidth: 0,
        borderRadius: 3.5,
        borderColor: theme.palette.divider,
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        p: { xs: 1.5, sm: 2 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          minWidth: 0,
        }}
      >
        <TextField
          id={`${id}-search`}
          label={t("search")}
          placeholder={t("searchPlaceholder")}
          size="small"
          value={filters.query}
          onChange={(event) => change({ query: event.target.value })}
          sx={{ flex: "1 1 18rem", minWidth: 0 }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon
                  fontSize="small"
                  sx={{ color: theme.palette.text.secondary }}
                />
              </InputAdornment>
            ),
            endAdornment: filters.query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t("clearSearch")}
                  onClick={() => change({ query: "" })}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <Button
          variant={expanded || activeCount > 0 ? "contained" : "outlined"}
          disableElevation
          startIcon={<FilterListRoundedIcon />}
          aria-expanded={expanded}
          aria-controls={`${id}-panel`}
          onClick={() => setExpanded(!expanded)}
          sx={{ minWidth: 0, whiteSpace: "normal" }}
        >
          {t("toggle", { count: activeCount })}
        </Button>
        <Button
          startIcon={<RestartAltRoundedIcon />}
          disabled={activeCount === 0 && !hasDraft}
          onClick={() => {
            const defaults = createDefaultAdminOverviewFilters();
            setDates({
              applied: { ...defaults.createdRange },
              draft: { ...defaults.createdRange },
              badFrom: false,
              badThrough: false,
            });
            fireEvent?.("filters-changed", defaults);
          }}
          sx={{ minWidth: 0, whiteSpace: "normal" }}
        >
          {t("reset")}
        </Button>
        <Typography
          role="status"
          aria-live="polite"
          aria-atomic="true"
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            overflowWrap: "anywhere",
            minWidth: 0,
          }}
        >
          {t("clientCount", { matched: matchedClients, total: totalClients })}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box
          id={`${id}-panel`}
          role="group"
          aria-label={t("title")}
          sx={{
            minWidth: 0,
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
              minWidth: 0,
            }}
          >
            {multiSelect("plans", PLANS, filters.plans)}
            {multiSelect("statuses", STATUSES, filters.statuses)}
            {multiSelect("health", HEALTH, filters.health)}
            <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
              <InputLabel id={`${id}-presence-label`}>
                {t("appPresence")}
              </InputLabel>
              <Select<AdminOverviewFilters["appPresence"]>
                id={`${id}-presence`}
                labelId={`${id}-presence-label`}
                label={t("appPresence")}
                value={filters.appPresence}
                onChange={(event) => {
                  const value = event.target.value;
                  if (
                    value === "all" ||
                    value === "with-apps" ||
                    value === "without-apps"
                  ) {
                    change({ appPresence: value });
                  }
                }}
                sx={{
                  "& .MuiSelect-select": {
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                  },
                }}
              >
                {(["all", "with-apps", "without-apps"] as const).map(
                  (value) => (
                    <MenuItem key={value} value={value}>
                      {t(`options.${value}`)}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            {(["from", "through"] as const).map((bound) => (
              <TextField
                key={bound}
                id={`${id}-${bound}`}
                type="date"
                label={t(bound)}
                size="small"
                fullWidth
                value={dates.draft[bound]}
                error={
                  !!dateError &&
                  (dateError === "from-after-through" ||
                    dateError === `invalid-${bound}`)
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  "aria-describedby": `${id}-date-note${dateError ? ` ${id}-date-error` : ""}`,
                }}
                onChange={(event) =>
                  editDate(
                    bound,
                    event.target.value,
                    (event.target as HTMLInputElement).validity.badInput
                  )
                }
                onInput={(event) => {
                  const input = event.target as HTMLInputElement;
                  const wasBad =
                    bound === "from" ? dates.badFrom : dates.badThrough;
                  // React's onChange ignores partial native input when its
                  // exposed value was already "". Still surface/clear badInput.
                  if (
                    input.value === dates.draft[bound] &&
                    input.validity.badInput !== wasBad
                  ) {
                    editDate(bound, input.value, input.validity.badInput);
                  }
                }}
                sx={{ minWidth: 0, "& input": { minWidth: 0 } }}
              />
            ))}
          </Box>
          <Typography
            id={`${id}-date-note`}
            variant="caption"
            component="p"
            sx={{ mt: 1, mb: 0, color: theme.palette.text.secondary }}
          >
            {t("dateNote")}
          </Typography>
          {dateError && (
            <FormHelperText id={`${id}-date-error`} error role="alert">
              {t(`errors.${dateError}`)} {t("lastValidRange")}
            </FormHelperText>
          )}
        </Box>
      </Collapse>
      {conflict && (
        <Typography
          variant="body2"
          role="note"
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 2,
            color: theme.palette.warning.dark,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            overflowWrap: "anywhere",
          }}
        >
          {t("appLessConflict")}
        </Typography>
      )}
    </Paper>
  );
};
